import { fontFor } from '../format'
import type { KvBlock } from '../spec/types'
import { Why } from './Why'
import { isJustification } from './justification'

/**
 * The state strip — a resolved fact, its qualifier, and where it came from.
 *
 * Verbatim from `docs/design/reference/blocks/kv.html`. A 1px gap over a lined
 * background is what draws the grid: each tile paints its own hairline with an
 * outset `box-shadow`, so the rules stay 1px however the columns reflow.
 *
 * This block is how the console refuses to state a fact bare. Every tile is
 * key, value, an optional tone chip, and a note that says who set it and when
 * — `set by us · reason n/a`, `FinXP acceptance 2025-11-06 · not the same as
 * IDV`. That fourth line is the point: an operator reading `accepted_by_emi`
 * has to be able to see, without a second query, that acceptance is the EMI's
 * word and our IDV result is a different claim entirely (invariant 2).
 *
 * The chip is emitted even when empty, as the guide does. An empty chip is a
 * 10px-wide, zero-height box, so it costs a fixed indent and paints nothing —
 * which keeps the value column landing in the same place whether or not a tile
 * is qualified.
 *
 * # The note is no longer always open, and the value is now the biggest thing
 *
 * An operator called this rendering "overwhelming" on Customer 360, and the
 * measurement agreed: two of these blocks delivered NINE VALUES behind about
 * 1,300 characters of permanently visible explanation. The value an operator
 * came for — `default v1` — was set in the same 11px grey as the paragraph
 * arguing for it, and there was more paragraph.
 *
 * Two changes, and only two:
 *
 *   - The value is 14px and semibold rather than 12.5px and medium. It is what
 *     the tile is for.
 *   - A note that is a JUSTIFICATION goes behind `Why`. A note that is a
 *     QUALIFIER stays exactly where it was.
 *
 * That second distinction is the whole care of this change. `set by us · reason
 * n/a` and `FinXP acceptance 2025-11-06 · not the same as IDV` are not
 * explanations — they are part of the claim, and the second is load-bearing for
 * invariant 2. A value whose qualifier is one click away is a value being
 * reported without its qualifier, which is worse than a dense screen. See
 * `isJustification` for how the line is drawn and why it is drawn there.
 */
export function Kv({ block }: { block: KvBlock }) {
  return (
    <div
      style={{
        border: '1px solid var(--k-line)',
        background: 'var(--k-panel)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))',
        gap: '1px',
      }}
    >
      {block.items.map((item, i) => (
        <div
          // Tile identity is positional in the design corpus; the key text
          // reads as an id but nothing in the type guarantees one, and the
          // item list is fixed and ordered.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{
            background: 'var(--k-panel)',
            padding: '8px 12px',
            boxShadow: '0 0 0 1px var(--k-line)',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--k-mut2)',
              fontFamily: 'var(--k-mono)',
            }}
          >
            {item[0]}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
            <span
              data-tone={item[3]}
              style={{
                padding: '0 5px',
                fontSize: '10px',
                fontWeight: 800,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
              }}
            >
              {item[2]}
            </span>
            <span
              style={{
                fontFamily: fontFor(item[5]),
                // The dominant element on the tile. It was 12.5px/500, the same
                // weight as the prose under it.
                fontSize: '14px',
                fontWeight: item[5] === 'b' ? 800 : 650,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item[1]}
            </span>
          </div>
          {item[4] !== '' && (
            isJustification(item[4])
              ? <Why>{item[4]}</Why>
              : (
                  <div style={{ fontSize: '11px', color: 'var(--k-mut)', marginTop: '2px' }}>
                    {item[4]}
                  </div>
                )
          )}
        </div>
      ))}
    </div>
  )
}
